import { RESPONSIVE_IMAGES } from "@/lib/images.generated";

/** Smallest AVIF/WebP variant URL, for preload links and low-cost thumbnails. */
export function smallestVariant(src: string): string | undefined {
  const entry = RESPONSIVE_IMAGES[src];
  return entry?.avif[0]?.url ?? entry?.webp[0]?.url;
}

/** Best variant at or above a target CSS width, used for LCP preloads. */
export function variantAtWidth(src: string, width: number, format: "avif" | "webp" = "avif"): string | undefined {
  const entry = RESPONSIVE_IMAGES[src];
  if (!entry) return undefined;
  const list = entry[format];
  return list.find((v) => v.width >= width)?.url ?? list[list.length - 1]?.url;
}

/**
 * `<link rel="preload">` descriptor for an LCP image.
 *
 * Preloads the AVIF candidate with the same srcset/sizes the <picture>
 * element uses, so the preload and the render resolve to the same file
 * instead of double-downloading. Browsers without AVIF support ignore the
 * link because of the `type` attribute and fall back to the WebP source.
 * Returns undefined when the URL has no generated variants.
 */
export function preloadImageLink(src: string, sizes: string) {
  const entry = RESPONSIVE_IMAGES[src];
  if (!entry || entry.avif.length === 0) return undefined;
  return {
    rel: "preload",
    as: "image",
    type: "image/avif",
    href: entry.avif[entry.avif.length - 1].url,
    imageSrcSet: entry.avif.map((v) => `${v.url} ${v.width}w`).join(", "),
    imageSizes: sizes,
    fetchPriority: "high",
  } as const;
}
